import { useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import api, { apiError } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { TRY, fmtDate, todayISO, sameOrNew } from "@/lib/format";
import { PageHeader, StatCard, Empty, Cell, Segmented } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { Combobox } from "@/components/Combobox";
import { ScrollText, ArrowDownCircle, ArrowUpCircle, CalendarClock, CheckCircle2, RotateCcw, Trash2, AlertTriangle, Pencil, Search, FileSpreadsheet, Link2, User, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const DAY = 86400000;
const daysLeft = (due, today) => Math.ceil((new Date(due) - new Date(today)) / DAY);

export default function Cheques() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [dir, setDir] = useState("all");
  const [due, setDue] = useState("all");
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState(null);
  const [invOptions, setInvOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/cheques").then((r) => setItems(sameOrNew(r.data))).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const today = todayISO();
  const monthPre = today.slice(0, 7);
  const soon = new Date(Date.now() + 7 * DAY).toISOString().slice(0, 10);
  const sum = (arr) => arr.reduce((s, c) => s + (c.amount || 0), 0);

  const pending = items.filter((c) => !c.cleared);
  const cleared = items.filter((c) => c.cleared);
  const isOverdue = (c) => !c.cleared && c.due_date && c.due_date < today;
  const isSoon = (c) => !c.cleared && c.due_date && c.due_date >= today && c.due_date <= soon;
  const pendingReceived = pending.filter((c) => c.direction === "gelen");
  const pendingGiven = pending.filter((c) => c.direction === "giden");
  const dueThisMonth = pending.filter((c) => (c.due_date || "").startsWith(monthPre));
  const overdue = items.filter(isOverdue);
  const soonList = items.filter(isSoon);

  const filtered = useMemo(() => {
    let arr = tab === "pending" ? pending : tab === "cleared" ? cleared : items;
    if (dir !== "all") arr = arr.filter((c) => c.direction === dir);
    if (due === "overdue") arr = arr.filter(isOverdue);
    else if (due === "soon") arr = arr.filter(isSoon);
    const q = search.trim().toLocaleLowerCase("tr");
    if (q) arr = arr.filter((c) => [c.contact_title, c.from_whom, c.serial_no, c.document_no, c.invoice_no, c.note].some((v) => String(v || "").toLocaleLowerCase("tr").includes(q)));
    const key = (c) => c.due_date || c.date || "";
    return [...arr].sort((a, b) => (tab === "cleared" ? key(b).localeCompare(key(a)) : key(a).localeCompare(key(b))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tab, dir, due, search]);

  const setStatus = async (id, isCleared) => {
    try { await api.put(`/cheques/${id}/status`, { cleared: isCleared }); toast.success(isCleared ? "Çek tahsil edildi olarak işaretlendi" : "Çek beklemeye alındı"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (c) => {
    if (!(await confirm({ title: "Çek kaydı silinsin mi?", description: `${c.contact_title} · ${TRY(c.amount)} · vade ${c.due_date ? fmtDate(c.due_date) : "—"}. Bu kayıt cari ödemelerinden de kaldırılır.`, destructive: true }))) return;
    try { await api.delete(`/payments/${c.id}`); toast.success("Çek kaydı silindi"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const exportExcel = () => {
    if (!filtered.length) return toast.error("Dışa aktarılacak çek yok");
    const ws = XLSX.utils.json_to_sheet(filtered.map((c) => ({
      "Vade": c.due_date ? fmtDate(c.due_date) : "", "Yön": c.direction === "gelen" ? "Alınan" : "Verilen", "Cari": c.contact_title || "",
      "Kimden / Kime": c.from_whom || "", "Seri No": c.serial_no || "", "Belge No": c.document_no || "", "İlişkili Fatura": c.invoice_no || "",
      "Tutar": Number(c.amount || 0), "Durum": c.cleared ? "Tahsil edildi" : isOverdue(c) ? "Vadesi geçti" : "Bekliyor", "Not": c.note || "",
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Çekler");
    XLSX.writeFile(wb, `cekler_${today}.xlsx`);
    toast.success(`${filtered.length} çek Excel olarak indirildi`);
  };

  const openEdit = async (c) => {
    setEdit({ ...c, amount: c.amount ?? 0, serial_no: c.serial_no || "", document_no: c.document_no || "", from_whom: c.from_whom || "", invoice_no: c.invoice_no || "" });
    setInvOptions([]);
    try {
      const { data } = await api.get("/invoices", { params: { contact: c.contact_title } });
      setInvOptions([
        { value: "", label: "— İlişkili fatura yok —" },
        ...data.map((inv) => ({ value: inv.invoice_no || inv.id, label: `${inv.type === "satis" ? "Satış" : "Alış"} #${inv.invoice_no || "—"} · ${TRY(inv.total)} · ${fmtDate(inv.date)}` })),
      ]);
    } catch { /* opsiyonel */ }
  };

  const saveEdit = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      const payload = {
        contact_title: edit.contact_title || "", direction: edit.direction || "gelen", amount: Number(edit.amount) || 0, method: "cek",
        date: edit.date || todayISO(), note: edit.note || "", due_date: edit.due_date || "", invoice_id: edit.invoice_id || "", invoice_no: edit.invoice_no || "",
        from_whom: edit.from_whom || "", serial_no: edit.serial_no || "", document_no: edit.document_no || "", cleared: !!edit.cleared,
      };
      await api.put(`/payments/${edit.id}`, payload);
      toast.success("Çek bilgileri güncellendi");
      setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const kpiClick = (t, d, dueKey = "all") => { setTab(t); setDir(d); setDue(dueKey); };
  const active = (t, d, dueKey) => tab === t && dir === d && due === dueKey;

  return (
    <div className="space-y-6 stagger-in" data-testid="cheques-page">
      <PageHeader title="Vadesi Gelmemiş Evraklar" badge="Çek Takibi" subtitle="Alınan / verilen çekler — vade, kimden/kime, seri & belge no, ilişkili cari ve fatura; vadesi yaklaşan ve geçen çekler otomatik vurgulanır">
        <Button variant="outline" onClick={exportExcel} data-testid="cheque-export-excel"><FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />Excel'e Aktar</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="cheque-kpis">
        <StatCard label="Bekleyen Alınan Çek" value={TRY(sum(pendingReceived))} icon={ArrowDownCircle} tone="success" sub={`${pendingReceived.length} çek`} testid="cheque-kpi-received" onClick={() => kpiClick("pending", "gelen")} active={active("pending", "gelen", "all")} />
        <StatCard label="Bekleyen Verilen Çek" value={TRY(sum(pendingGiven))} icon={ArrowUpCircle} tone="blue" sub={`${pendingGiven.length} çek`} testid="cheque-kpi-given" onClick={() => kpiClick("pending", "giden")} active={active("pending", "giden", "all")} />
        <StatCard label="7 Gün İçinde Vade" value={soonList.length} icon={Hourglass} tone="warning" sub={TRY(sum(soonList))} testid="cheque-kpi-soon" onClick={() => kpiClick("pending", "all", "soon")} active={active("pending", "all", "soon")} />
        <StatCard label="Vadesi Geçen" value={overdue.length} icon={AlertTriangle} tone={overdue.length ? "danger" : "default"} sub={TRY(sum(overdue))} testid="cheque-kpi-overdue" onClick={() => kpiClick("pending", "all", "overdue")} active={active("pending", "all", "overdue")} />
        <StatCard label="Bu Ay Vadesi Gelen" value={dueThisMonth.length} icon={CalendarClock} tone="primary" sub={TRY(sum(dueThisMonth))} testid="cheque-kpi-month" />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3" data-testid="cheque-toolbar">
        <Segmented value={tab} onChange={setTab} testid="cheque-tabs" options={[["pending", "Bekleyen", pending.length], ["cleared", "Tahsil Edilenler", cleared.length], ["all", "Tümü", items.length]]} />
        <Segmented value={dir} onChange={setDir} size="xs" testid="cheque-dir" options={[["all", "Tüm yönler"], ["gelen", "Alınan"], ["giden", "Verilen"]]} />
        <Segmented value={due} onChange={setDue} size="xs" testid="cheque-due" options={[["all", "Tüm vadeler"], ["soon", "7 gün", soonList.length], ["overdue", "Vadesi geçen", overdue.length]]} />
        <div className="relative flex-1 lg:max-w-xs lg:ml-auto">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari, kimden, seri/belge no, fatura ara..." className="pl-9 h-9" data-testid="cheque-search" />
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground px-1" data-testid="cheque-list-summary">
        <span><b className="text-foreground tabular-nums">{filtered.length}</b> çek listeleniyor</span>
        <span className="tabular-nums">Liste toplamı: <b className="text-foreground">{TRY(sum(filtered))}</b></span>
      </div>

      {filtered.length === 0 ? (
        <Empty title={items.length ? "Eşleşen çek yok" : "Çek kaydı yok"} desc={items.length ? "Filtreyi veya aramayı değiştirin." : "Cari Hesaplar'da bir tahsilat/ödeme eklerken yöntem olarak 'Çek' seçtiğinizde buraya düşer."} />
      ) : (
        <div className="space-y-2.5" data-testid="cheque-list">
          {filtered.map((c) => {
            const od = isOverdue(c), sn = isSoon(c);
            const stripe = c.cleared ? "bg-[#16A34A]" : od ? "bg-destructive" : sn ? "bg-amber-500" : c.direction === "gelen" ? "bg-emerald-300" : "bg-secondary/60";
            const left = c.due_date ? daysLeft(c.due_date, today) : null;
            return (
              <div key={c.id} className={`group relative rounded-xl border bg-card pl-5 pr-4 py-3.5 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px ${od ? "border-red-200 bg-red-50/40" : sn ? "border-amber-200 bg-amber-50/40" : ""}`} data-testid={`cheque-${c.id}`}>
                <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${stripe}`} />
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-6 gap-3 min-w-0">
                    <Cell label="Vade">
                      <div className="flex items-center gap-1.5 font-head font-black text-[15px] tabular-nums"><ScrollText className="h-3.5 w-3.5 text-muted-foreground" />{c.due_date ? fmtDate(c.due_date) : "—"}</div>
                      {od && <span className="text-[10px] text-destructive font-bold" data-testid={`cheque-overdue-${c.id}`}>Vadesi {Math.abs(left)} gün geçti</span>}
                      {sn && <span className="text-[10px] text-amber-600 font-bold" data-testid={`cheque-soon-${c.id}`}>{left === 0 ? "Bugün vadesi geliyor" : `Çek tahsilatına ${left} gün kaldı`}</span>}
                      {!od && !sn && !c.cleared && left != null && <span className="text-[10px] text-muted-foreground">{left} gün sonra</span>}
                      {c.cleared && <span className="text-[10px] text-emerald-700 font-semibold">Tahsil edildi</span>}
                    </Cell>
                    <Cell label="Yön · Cari" className="col-span-2 md:col-span-1">
                      {c.direction === "gelen"
                        ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px] mb-0.5"><ArrowDownCircle className="h-3 w-3 mr-1" />Alınan</Badge>
                        : <Badge variant="outline" className="border-secondary/40 bg-secondary/10 text-secondary text-[10px] mb-0.5"><ArrowUpCircle className="h-3 w-3 mr-1" />Verilen</Badge>}
                      <div className="font-head font-black text-[15px] leading-tight truncate" title={c.contact_title}>{c.contact_title || "—"}</div>
                      {c.contact_phone && <div className="text-[11px] text-muted-foreground">{c.contact_phone}</div>}
                    </Cell>
                    <Cell label={c.direction === "gelen" ? "Kimden Alındı" : "Kime Verildi"}>
                      <div className="text-sm font-semibold truncate inline-flex items-center gap-1" title={c.from_whom}><User className="h-3 w-3 text-muted-foreground" />{c.from_whom || "—"}</div>
                    </Cell>
                    <Cell label="Seri / Belge No">
                      <div className="font-mono text-xs"><span className="text-muted-foreground">S:</span> {c.serial_no || "—"}</div>
                      <div className="font-mono text-xs"><span className="text-muted-foreground">B:</span> {c.document_no || "—"}</div>
                    </Cell>
                    <Cell label="İlişkili Fatura">
                      {c.invoice_no ? <span className="inline-flex items-center gap-1 font-mono text-xs font-bold text-secondary"><Link2 className="h-3 w-3" />{c.invoice_no}</span> : <span className="text-xs text-muted-foreground">Cariye mahsuben</span>}
                      {c.note && <div className="text-[11px] text-muted-foreground truncate" title={c.note}>{c.note}</div>}
                    </Cell>
                    <Cell label="Tutar · Durum">
                      <div className={`font-head font-black text-[17px] tabular-nums ${c.direction === "gelen" ? "text-[#16A34A]" : "text-secondary"}`} data-testid={`cheque-amount-${c.id}`}>{TRY(c.amount)}</div>
                      {c.cleared
                        ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Tahsil edildi</Badge>
                        : od ? <Badge variant="destructive" className="text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />Vadesi geçti</Badge>
                        : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]"><Hourglass className="h-3 w-3 mr-1" />Bekliyor</Badge>}
                    </Cell>
                  </div>
                  <div className="flex items-center gap-1 lg:justify-end shrink-0 lg:pl-3 lg:border-l">
                    {c.cleared
                      ? <Button size="sm" variant="outline" className="h-8" onClick={() => setStatus(c.id, false)} data-testid={`cheque-unclear-${c.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Beklemeye Al</Button>
                      : <Button size="sm" className="h-8 bg-[#16A34A] hover:bg-[#15803d]" onClick={() => setStatus(c.id, true)} data-testid={`cheque-clear-${c.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Tahsil Edildi</Button>}
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Düzenle" onClick={() => openEdit(c)} data-testid={`cheque-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Sil" onClick={() => del(c)} data-testid={`cheque-del-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="cheque-edit-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl">Çek Bilgilerini Düzenle</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 rounded-lg bg-muted/50 px-4 py-2.5 text-sm font-semibold flex items-center justify-between"><span>{edit.contact_title}</span><span className="font-mono text-xs text-muted-foreground">{edit.id?.slice(0, 8)}</span></div>
              <Fld label="Yön">
                <Select value={edit.direction} onValueChange={(v) => setEdit({ ...edit, direction: v })}>
                  <SelectTrigger data-testid="cheque-edit-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gelen">Alınan Çek (müşteriden)</SelectItem>
                    <SelectItem value="giden">Verilen Çek (tedarikçiye)</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
              <Fld label="Tutar (₺)"><Input type="number" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} className="font-head font-black tabular-nums" data-testid="cheque-edit-amount" /></Fld>
              <Fld label="Çek Vadesi"><DatePicker value={edit.due_date || ""} onChange={(v) => setEdit({ ...edit, due_date: v })} placeholder="Vade seçin" data-testid="cheque-edit-due" /></Fld>
              <Fld label={edit.direction === "gelen" ? "Kimden Alındı" : "Kime Verildi"}><Input value={edit.from_whom} onChange={(e) => setEdit({ ...edit, from_whom: e.target.value })} placeholder="Ör. Ahmet Yılmaz" data-testid="cheque-edit-fromwhom" /></Fld>
              <Fld label="Çek Seri No"><Input value={edit.serial_no} onChange={(e) => setEdit({ ...edit, serial_no: e.target.value })} placeholder="Seri no" data-testid="cheque-edit-serial" /></Fld>
              <Fld label="Belge / Evrak No"><Input value={edit.document_no} onChange={(e) => setEdit({ ...edit, document_no: e.target.value })} placeholder="Belge no" data-testid="cheque-edit-document" /></Fld>
              <Fld className="sm:col-span-2" label="İlişkili Fatura (sonradan seçilebilir)">
                <Combobox options={invOptions} value={edit.invoice_no || ""} onChange={(v) => setEdit({ ...edit, invoice_no: v })} allowCustom placeholder="Fatura seçin / no yazın" searchPlaceholder="Fatura ara..." data-testid="cheque-edit-invoice" />
              </Fld>
              <Fld className="sm:col-span-2" label="Not"><Input value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} data-testid="cheque-edit-note" /></Fld>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>İptal</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="cheque-edit-save">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Fld = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</Label>
    {children}
  </div>
);
